import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Printer, ArrowLeft } from "lucide-react";
import { adminList } from "@/lib/api";
import { REMOVAL_WINDOW, humanSlot, addonsSummary } from "@/data/collections";
import { MapLink } from "@/components/AdminDetails";

const TOKEN_KEY = "pp_admin_token";
const fmtShort = (iso) => new Date(iso + "T12:00:00").toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
const fmtLong = (iso) => new Date(iso + "T12:00:00").toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric", year: "numeric" });

export default function PrintRemovals() {
  const [sp] = useSearchParams();
  const date = sp.get("date") || "";
  const [bookings, setBookings] = useState(null);
  const [error, setError] = useState("");
  const token = localStorage.getItem(TOKEN_KEY) || "";

  useEffect(() => {
    if (!token) { setError("Please sign in to the dashboard first."); return; }
    adminList(token).then((r) => setBookings(r.bookings || [])).catch(() => setError("Could not load bookings."));
  }, [token]);

  const items = useMemo(() => (bookings || []).filter((b) => b.priority_removal && b.status !== "denied" && (!date || b.removal_date === date)).sort((a, b) => (a.removal_date || "9").localeCompare(b.removal_date || "9") || a.date.localeCompare(b.date)), [bookings, date]);

  return (
    <div className="min-h-screen bg-white text-black">
      <style>{`@media print { .no-print, a[data-testid^="print-removal-map-"] { display: none !important; } body { background: #fff; } @page { margin: 14mm; } }`}</style>
      <div className="no-print sticky top-0 flex items-center justify-between gap-4 px-6 py-3 border-b" style={{ backgroundColor: "var(--pp-cream)", borderColor: "var(--pp-border)" }}>
        <Link to="/admin" data-testid="print-removals-back" className="pp-btn-ghost" style={{ color: "var(--pp-text-muted)" }}><ArrowLeft className="h-4 w-4" /> Dashboard</Link>
        <button onClick={() => window.print()} data-testid="print-removals-button" className="pp-btn !rounded-full !py-2 !px-5 !text-[0.7rem]"><Printer className="h-4 w-4" /> Print / Save PDF</button>
      </div>
      <main className="max-w-3xl mx-auto px-6 py-10" data-testid="print-removals-sheet">
        <header className="flex items-end justify-between border-b-2 border-black pb-3 mb-6">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">Pumpkin &amp; Porch · {date ? "Removal day sheet" : "Priority Removal list"}</div>
            <h1 className="font-serif-display text-4xl leading-tight" data-testid="print-removals-title">{date ? fmtLong(date) : `Removals ${REMOVAL_WINDOW.label}`}</h1>
          </div>
          <div className="text-right text-xs text-neutral-600">{items.length} {date ? "pickup" : "customer"}{items.length === 1 ? "" : "s"}</div>
        </header>
        {error && <p className="text-sm text-red-700">{error}</p>}
        {bookings && items.length === 0 && !error && <p className="text-sm text-neutral-600" data-testid="print-removals-empty">{date ? "No removals scheduled for this day." : "No Priority Removal bookings yet."}</p>}
        <ol className="space-y-4">
          {items.map((b, i) => (
            <li key={b.id} className="break-inside-avoid border border-neutral-300 p-4" data-testid={`print-removal-${b.reference}`}>
              <div className="flex items-baseline justify-between gap-4 border-b border-neutral-200 pb-2 mb-2">
                <div className="font-serif-display text-2xl">{i + 1}. {b.removal_date && !date ? `${fmtShort(b.removal_date)} — ` : ""}{b.full_name}</div>
                <div className="text-xs uppercase tracking-widest text-neutral-500">{b.reference} · {b.status} · {b.deposit_paid ? "Paid" : "Unpaid"}</div>
              </div>
              <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
                <Field k="Address" v={<MapLink address={b.address} testId={`print-removal-map-${b.reference}`} />} wide />
                <Field k="Phone" v={b.phone} />
                <Field k="Email" v={b.email} />
                <Field k="Installed" v={`${b.date} · ${humanSlot(b.time_slot)}`} />
                <Field k="Package" v={`${b.collection_name} · ${b.size_label}`} />
                <Field k="Items" v={addonsSummary(b)} wide />
                {b.customer_notes && <Field k="Customer notes" v={b.customer_notes} wide />}
                {b.notes && <Field k="Private notes" v={b.notes} wide />}
              </div>
              <div className="mt-3 text-xs text-neutral-500">Removal date: {b.removal_date || "______________"}  Crew notes: ______________________________________</div>
            </li>
          ))}
        </ol>
      </main>
    </div>
  );
}

const Field = ({ k, v, wide }) => (
  <div className={wide ? "col-span-2" : ""}>
    <span className="text-[10px] uppercase tracking-widest text-neutral-500 mr-2">{k}</span>
    <span>{v}</span>
  </div>
);
